import { config } from '../../config';
import FeedDetail from '../components/Feed/FeedDetail';
import FeedLegend from '../components/Feed/FeedLegend';
import FeedPanel from '../components/Feed/FeedPanel';
import type { FeedSegmentValue } from '../components/Feed/FeedSegment';
import FeedSheet, {
  SHEET_COLLAPSED,
  SHEET_EXPANDED,
} from '../components/Feed/FeedSheet';
import FeedSortOrder, {
  sortOptions,
  type FeedSortOrderValues,
} from '../components/Feed/FeedSortOrder';
import {
  FEED_STATS,
  matchesStats,
  type FeedStatKey,
} from '../components/Feed/feedStatus';
import type { IFeedItem } from '../components/FeedItem/FeedItem';
import FeedsMap from '../components/FeedsMap';
import NewReportFab from '../components/NewReportFab';
import { useCityBoundary } from '../features/queries/getCityBoundary';
import { getFeed } from '../features/queries/getFeed';
import { meGetNewsCategories } from '../features/queries/meGetNewsCategories';
import { ID_PREFIX, shortId } from '../lib/ids';
import { plusCodeToCoordinates } from '../lib/plusCode';
import { useInfiniteQ, useQ } from '../lib/query';
import { useHomePage } from '../providers/HomePageProvider';
import { useGeoLocation } from '../providers/LocationProvider';
import { useResponsive } from '../providers/ResponsiveProvider';
import { __, hasLocation, toastError } from '../utils';
import { useAuth } from '@presov.sk/presov-auth-react';
import { Box } from '@presov.sk/presov-ui-react';
import React from 'react';
import { useMatch, useNavigate, useSearchParams } from 'react-router';

export interface HomePageProps {
  plusCode?: string;
}

/** Je to identifikátor podnetu (`pdnt:…`), nie novinky? Znesie všetky tvary z [[ids]]. */
const isReportId = (id: string) =>
  id.split(':').slice(-2, -1)[0] === ID_PREFIX.report;

/**
 * Predvoľba zo zadania: to, čo ešte nie je hotové, **plus vlastné podnety**.
 *
 * `mine` tu musí byť odkedy sú bunky oddelené kôpky (`matchesStat`): vlastný
 * podnet sa už do „Nové" ani „V riešení" nezapočíta, takže bez tejto tretej
 * bunky by ho autor po prihlásení vo východiskovom zobrazení nevidel vôbec.
 */
const DEFAULT_STAT_FILTERS: FeedStatKey[] = ['open', 'accepted', 'mine'];

/**
 * Priblíženie pre odkaz na miesto (plus kód z detailu podnetu). O stupeň menej
 * než detail (17 v [[FeedsMap]]): cieľom nie je jedna značka, ale jej okolie
 * - zopár ulíc, na ktorých sú susedné podnety ešte rozlíšiteľné.
 */
const NEARBY_ZOOM = 16;

/**
 * Zaškrtnuté bunky štatistiky z `localStorage`.
 *
 * Overuje sa obsah, nie len tvar: kľúče buniek sa môžu premenovať alebo ubrať
 * a v prehliadačoch používateľov leží uložený zoznam ďalej. Neznámy kľúč sa
 * zahodí, prázdny zoznam je platná voľba (nič nezaškrtnuté = celé okolie),
 * takže na predvoľbu padá len chýbajúca alebo pokazená hodnota.
 */
const readStoredStatFilters = (): FeedStatKey[] => {
  const stored = localStorage.getItem(config.LOCAL_STORAGE.FEED_STAT_FILTERS);
  if (!stored) return DEFAULT_STAT_FILTERS;

  try {
    const parsed: unknown = JSON.parse(stored);
    if (!Array.isArray(parsed)) return DEFAULT_STAT_FILTERS;

    const known = FEED_STATS.map(({ key }) => key);
    return parsed.filter((key): key is FeedStatKey =>
      known.includes(key as FeedStatKey),
    );
  } catch {
    return DEFAULT_STAT_FILTERS;
  }
};

const HomePage: React.FC<HomePageProps> = ({ plusCode: plusCodeProp }) => {
  const [searchParams] = useSearchParams();
  const { locationStatus } = useGeoLocation();
  const { isInside } = useCityBoundary();
  const { authUser, authState } = useAuth();
  const { isMd } = useResponsive();
  const {
    location: searchLocation,
    setLocation: setSearchLocation,
    hasInitialFetched,
    setHasInitialFetched,
    mapCenter,
    setMapCenter,
    sheetExpanded,
    feedScroll,
    setSheetExpanded,
    zoom,
    setZoom,
  } = useHomePage();

  // Načítanie lat a lon z query parametrov
  const queryLat = searchParams.get('lat');
  const queryLon = searchParams.get('lon');
  // plusCode môže prísť ako prop (z /plus.code/:plusCode route) alebo z query
  // parametra. Tvar sa tu nenormalizuje - medzeru namiesto `+`, skrátený aj
  // zložený kód rieši `plusCodeToCoordinates`, a to je jediné miesto, ktoré má
  // vedieť, aké tvary plus kódu existujú.
  const queryPlusCode = plusCodeProp || searchParams.get('plusCode');

  const localStorageSortOrder = JSON.parse(
    localStorage.getItem(config.LOCAL_STORAGE.FEED_SORT_ORDER),
  ) || {
    orderBy: 'date',
    orderDirection: 'desc',
  };

  /**
   * Otvorený detail žije v adrese, nie v `useState`: dizajn žiada, aby sa naň
   * dal poslať odkaz, a zároveň tým obnovenie stránky nezhodí to, na čo sa
   * používateľ práve pozerá.
   *
   * **Podnet má vlastnú cestu `/podnety/<ID>`**, novinka ostáva na
   * `/?detail=<ID>` (jej `/novinky/<ID>` je samostatná stránka). Jedna adresa
   * na jeden obsah: tá istá, na ktorú vedie zdieľanie a pre ktorú nginx pýta
   * od digiobcan-api náhľad pre sociálne siete. Mapa sa pri tom znova
   * nekreslí - `/` aj `/podnety/:reportId` sú deti tej istej layout routy
   * v [[App]], takže táto stránka zostáva namontovaná.
   *
   * V adrese stojí **krátke ID** (`MQRJ73F`) rovnako ako všade inde v appke.
   * Porovnáva sa koncový segment, ktorý je jednoznačný sám o sebe - panel
   * otvára aj novinky (`spr:`), takže by uhádnutá predpona položku nenašla.
   * Starý tvar `/?detail=<podnet>` (odkazy z e-mailov a notifikácií) ďalej
   * funguje a efekt nižšie ho prepíše na cestu.
   */
  const navigate = useNavigate();
  const reportMatch = useMatch('/podnety/:reportId');
  const pathReportId = reportMatch?.params.reportId;
  const selectedShortId = pathReportId
    ? shortId(pathReportId)
    : (searchParams.get('detail') ?? undefined);

  const setSelectedNewsItemId = React.useCallback(
    (itemId?: string) => {
      // Ostatné parametre (poloha, plus kód) si detail nesie so sebou - len
      // `detail` patrí k novinke a k podnetu už nie.
      const params = new URLSearchParams(searchParams);
      params.delete('detail');

      let pathname = '/';
      if (itemId && isReportId(itemId)) {
        pathname = `/podnety/${shortId(itemId)}`;
      } else if (itemId) {
        params.set('detail', shortId(itemId));
      }

      const search = params.toString();
      // Výber značky nie je krok navigácie - inak by tlačidlo Späť
      // v prehliadači preklikávalo značky namiesto odchodu z mapy.
      navigate(
        { pathname, search: search ? `?${search}` : '' },
        {
          replace: true,
        },
      );
    },
    [navigate, searchParams],
  );
  const [mapView, setMapView] = React.useState<__esri.MapView | null>(null);
  // Žiadosť o presun mapy na položku vybranú v zozname. Drží sa oddelene od
  // výberu: mapa sa presunie len na ňu, nie na značku, na ktorú sa práve
  // klikol. Nový objekt pri každom kliknutí je zámer - druhé kliknutie na tú
  // istú položku má mapu presunúť znova.
  const [focusRequest, setFocusRequest] = React.useState<
    { id: string } | undefined
  >(undefined);
  // Segment (Podnety / Novinky mesta) a bunka štatistiky nahradili tri
  // nezávislé zaškrtávacie filtre. Segment rozhoduje, čo sa **ťahá** z API;
  // bunka štatistiky zužuje už načítaný zoznam, takže na ňu netreba dotaz.
  const [segment, setSegment] = React.useState<FeedSegmentValue>(() =>
    // Poloha v adrese je odkaz na podnety v okolí (plus kód z detailu
    // podnetu) - naposledy zvolené Novinky by ho zahodili.
    (queryLat && queryLon) || queryPlusCode
      ? 'reports'
      : (localStorage.getItem(
          config.LOCAL_STORAGE.FEED_SEGMENT,
        ) as FeedSegmentValue) || 'reports',
  );
  /**
   * Zaškrtnuté bunky štatistiky. Držia sa **naposledy zvolené**, nie predvolené
   * pri každom otvorení: kto sleduje len nevyriešené, nemá si to preklikávať
   * znova pri každom príchode. Predvoľba zo zadania (to, čo ešte nie je hotové)
   * platí len pri prvom otvorení, kým v prehliadači nič nie je.
   *
   * Ukladá sa do `localStorage`, **nie do adresy**: je to nastavenie
   * používateľa, nie stav, na ktorý sa posiela odkaz. Odkaz z hlavnej stránky
   * nesie polohu a otvorený detail, a tie v adrese ostávajú.
   */
  const [statFilters, setStatFilters] = React.useState<FeedStatKey[]>(() =>
    readStoredStatFilters(),
  );

  const toggleStatFilter = React.useCallback((key: FeedStatKey) => {
    setStatFilters((current) => {
      const next = current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key];
      localStorage.setItem(
        config.LOCAL_STORAGE.FEED_STAT_FILTERS,
        JSON.stringify(next),
      );
      return next;
    });
  }, []);
  // Vybraná oblasť na mape: identifikátory položiek z kliknutého zoskupenia.
  const [clusterIds, setClusterIds] = React.useState<string[] | null>(null);
  const [sortOrderOpen, setSortOrderOpen] = React.useState(false);
  const [sortOrder, setSortOrder] = React.useState<FeedSortOrderValues>(
    localStorageSortOrder,
  );
  const previousFeedDataRef = React.useRef<IFeedItem[]>([]);

  // Banner cookies sa odsadzuje o šírku tohto panela, aby naň nesadol (viď
  // src/lib/cookies.ts). Uhádnuť ju nemôže: panel má `flex: 1`, čím mu
  // `flex-basis: 0%` prebije `width: 400` a na širokých obrazovkách vyrastie
  // nad `minWidth` - pevných 400 px by dalo správnu medzeru len na md. Preto
  // skutočnú šírku meriame a vystavujeme ako CSS premennú.
  //
  // To isté číslo dostane aj mapa ako pravý okraj pohľadu (`viewPadding`),
  // aby stred mapy a pin v ňom boli v strede voľnej plochy, nie pod panelom.
  const feedPanelRef = React.useRef<HTMLDivElement>(null);
  const [feedPanelInset, setFeedPanelInset] = React.useState(0);
  React.useEffect(() => {
    const panel = feedPanelRef.current;
    if (!panel) {
      return;
    }
    const observer = new ResizeObserver(() => {
      const rect = panel.getBoundingClientRect();
      // Nie šírka panela, ale vzdialenosť jeho ľavej hrany od pravého okraja
      // okna: panel odteraz pláva 20 px od hrany, takže o samotnú šírku
      // odsadený banner by mu sadol na roh.
      const inset = Math.round(Math.max(0, window.innerWidth - rect.left));
      document.documentElement.style.setProperty(
        '--feed-panel-width',
        `${inset}px`,
      );
      setFeedPanelInset(inset);
    });
    observer.observe(panel);
    return () => {
      observer.disconnect();
      // Bez odobrania by banner zostal odsadený aj na stránkach bez panela.
      document.documentElement.style.removeProperty('--feed-panel-width');
    };
    // Panel je na mobile iný uzol (a v mape žiadny), takže sa pozorovateľ musí
    // pri zmene breakpointu prevesiť - inak by meral zaniknutý element.
  }, [isMd]);

  /**
   * Plus kód z adresy na súradnicu, ktorá dáva v tejto appke zmysel.
   *
   * Okrem nezmyselného kódu (ten `decode` ticho preloží na Londýn) padne na
   * stred Prešova aj **platný kód mimo mesta**: appka je o Prešove, mimo neho
   * nie je čo ukázať ani nahlásiť. Kým hranica nedorazí z API, `isInside`
   * púšťa všetko - vtedy sa to správa ako predtým.
   */
  const plusCodeInCity = React.useCallback(
    (code: string): [number, number] => {
      const decoded = plusCodeToCoordinates(code, config.DEFAULT_POSITION);
      return decoded && isInside(decoded[0], decoded[1])
        ? decoded
        : config.DEFAULT_POSITION;
    },
    [isInside],
  );

  // Nastavenie počiatočného centra mapy z query parametrov
  React.useEffect(() => {
    // Najprv skúsime lat/lon
    if (queryLat && queryLon) {
      const lat = parseFloat(queryLat);
      const lon = parseFloat(queryLon);
      if (!isNaN(lat) && !isNaN(lon)) {
        setMapCenter({ lat, lng: lon });
        setSearchLocation(`${lon},${lat}`);
        return;
      }
    }

    // Ak nemáme lat/lon, ale máme plusCode, dekódujeme ho. Neplatný kód
    // (aj taký, ktorý `decode` ticho preloží na Londýn) padne na Prešov -
    // a rovnako dopadne aj platný kód, ktorý ukazuje mimo mesta.
    if (queryPlusCode) {
      const [lng, lat] = plusCodeInCity(queryPlusCode);
      setMapCenter({ lat, lng });
      setSearchLocation(`${lng},${lat}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Výpočet queryLocation pre feed query
  const queryLocation = React.useMemo(() => {
    // Najprv skúsime lat/lon
    if (queryLat && queryLon) {
      return `${queryLon},${queryLat}`;
    }

    if (queryPlusCode) {
      const [lng, lat] = plusCodeInCity(queryPlusCode);
      return `${lng},${lat}`;
    }

    return undefined;
  }, [queryLat, queryLon, queryPlusCode, plusCodeInCity]);

  /**
   * Poloha z adresy ako let mapy - s priblížením, na ktorom je vidieť okolie
   * jednej ulice. Stred sám nestačil: mapa ostala na priblížení celého mesta
   * (13), takže „podnety v okolí" boli jedna kopa zoskupených značiek.
   * Kľúčom je reťazec `queryLocation`, takže sa let nezopakuje pri každom
   * renderi - len keď sa poloha v adrese naozaj zmení.
   */
  const placeRequest = React.useMemo(() => {
    if (!queryLocation) return undefined;
    const [lng, lat] = queryLocation.split(',').map(Number);
    return { center: [lng, lat] as [number, number], zoom: NEARBY_ZOOM };
  }, [queryLocation]);

  // Segment rozhoduje, čo sa ťahá: novinky, alebo podnety (verejné aj moje
  // naraz - „Moje" je bunka štatistiky nad tým istým zoznamom, nie iný dotaz).
  const queryFilter = React.useMemo(
    () => ({
      news: segment === 'news',
      userReports: segment === 'reports' && !!authUser,
      publicReports: segment === 'reports',
    }),
    [segment, authUser],
  );

  const { data: dataUserPreferences, isLoading: isLoadingUserPreferences } =
    useQ(meGetNewsCategories, undefined, {
      enabled: authState === 'authorized',
    });

  const {
    data = { pages: [] },
    isLoading: isFeedLoading,
    isFetching: isFeedFetching,
    isError: isFeedError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQ(
    getFeed,
    {
      location: queryLocation || searchLocation,
      category: dataUserPreferences,
      orderBy: sortOrder.orderBy,
      orderDirection: sortOrder.orderDirection,
      filter: queryFilter,
      isPublic: !authUser,
    },
    {
      enabled: !!(queryLocation || searchLocation) && !isLoadingUserPreferences,
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        if (
          !lastPage ||
          (!lastPage.nextCursors.news &&
            !lastPage.nextCursors.userReports &&
            !lastPage.nextCursors.publicReports)
        ) {
          return undefined;
        }

        return lastPage.nextCursors;
      },
    },
  );

  const handleMapCenterChange = React.useCallback(
    (center: { lng: number; lat: number }) => {
      setMapCenter(center);
    },
    [setMapCenter],
  );

  const sortByDistance = React.useCallback(
    (a: IFeedItem, b: IFeedItem) => {
      if (!mapCenter) return 0;
      const toRad = (value: number) => (value * Math.PI) / 180;
      const R = 6371;

      const getLatLng = (item: IFeedItem) => {
        const coords = item.location?.coordinates;
        if (!coords) return null;
        return { lat: coords[1], lng: coords[0] };
      };

      const dist = (loc: { lat: number; lng: number } | null) => {
        if (!loc) return Number.MAX_SAFE_INTEGER;
        const dLat = toRad(loc.lat - mapCenter.lat);
        const dLng = toRad(loc.lng - mapCenter.lng);
        const lat1 = toRad(mapCenter.lat);
        const lat2 = toRad(loc.lat);

        const a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.sin(dLng / 2) *
            Math.sin(dLng / 2) *
            Math.cos(lat1) *
            Math.cos(lat2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
      };

      return dist(getLatLng(a)) - dist(getLatLng(b));
    },
    [mapCenter],
  );

  React.useEffect(() => {
    if (isFeedError) {
      toastError(__('Nepodarilo sa načítať Novinky.'));
    }
  }, [isFeedError]);

  React.useEffect(() => {
    const storedSortOrder = localStorage.getItem(
      config.LOCAL_STORAGE.FEED_SORT_ORDER,
    );

    if (storedSortOrder) {
      return;
    }

    if (locationStatus === 'DENY' || locationStatus === 'UNAVAILABLE') {
      setSortOrder({
        orderBy: 'date',
        orderDirection: 'desc',
      });
    } else if (locationStatus === 'AVAILABLE') {
      setSortOrder({
        orderBy: 'location',
        orderDirection: 'asc',
      });
    }
  }, [locationStatus]);

  // Ak sú v query parametroch lat/lon alebo plusCode, nastav zoraďovanie na "od najbližších"
  React.useEffect(() => {
    if ((queryLat && queryLon) || queryPlusCode) {
      setSortOrder({
        orderBy: 'location',
        orderDirection: 'asc',
      });
    }
  }, [queryLat, queryLon, queryPlusCode]);

  const feedData = React.useMemo(() => {
    let items = data.pages.flatMap(({ items }) => items);

    // Novinky majú poradie zo servera (od najnovšej) a prepínač zoradenia
    // v ich segmente nie je. Bez tejto podmienky by ich preradilo zoradenie
    // naposledy zvolené v Podnetoch - podľa hlasov, ktoré novinky nemajú,
    // alebo podľa vzdialenosti, ktorú väčšina nemá.
    if (segment === 'news') {
      return items;
    }

    if (sortOrder.orderBy === 'location' && mapCenter) {
      items = items.sort(sortByDistance);
    } else if (sortOrder.orderBy === 'date') {
      // Pre date triedenie zoradíme podľa dátumu
      items = items.sort((a, b) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;

        if (sortOrder.orderDirection === 'desc') {
          return dateB - dateA;
        } else {
          return dateA - dateB;
        }
      });
    } else if (sortOrder.orderBy === 'votes') {
      // Podla poctu hlasov sa daju radit len podnety
      items = items.sort((a, b) => {
        // Novinky hlasy nemaju, tak skoncia na konci
        const votesA = a.votesCount || 0;
        const votesB = b.votesCount || 0;

        if (sortOrder.orderDirection === 'desc') {
          return votesB - votesA;
        } else {
          return votesA - votesB;
        }
      });
    }

    return items;
  }, [data.pages, mapCenter, segment, sortByDistance, sortOrder]);

  React.useEffect(() => {
    if (feedData.length > 0 && !hasInitialFetched) {
      setHasInitialFetched(true);
    }
  }, [feedData, hasInitialFetched, setHasInitialFetched]);

  const shouldShowLoading = React.useMemo(() => {
    if (!searchLocation) return true;
    if (!hasInitialFetched && isFeedLoading) return true;
    return false;
  }, [searchLocation, hasInitialFetched, isFeedLoading]);

  const stableFeedData = React.useMemo(() => {
    if (feedData.length > 0) {
      previousFeedDataRef.current = feedData;
      return feedData;
    }

    // Počas načítavania použijeme staré dáta, ale aplikujeme aktuálne filtre
    if (isFeedLoading || isFeedFetching) {
      return previousFeedDataRef.current.filter((item) =>
        segment === 'news' ? item.type === 'news' : item.type !== 'news',
      );
    }

    return feedData;
  }, [feedData, isFeedLoading, isFeedFetching, segment]);

  /**
   * Okolie, ktoré panel ukazuje, ešte **pred** filtrami stavu. Z neho sa
   * počítajú čísla v bunkách štatistiky, takže sa nehýbu podľa toho, čo mám
   * zaškrtnuté. Vybraná oblasť ho zužuje - to nie je filter, ale rozsah:
   * po kliknutí na zoskupenie hovorí panel o tej oblasti, nie o celom výreze.
   */
  const statItems = React.useMemo(() => {
    if (!clusterIds) return stableFeedData;
    const cluster = new Set(clusterIds);
    return stableFeedData.filter((item) => cluster.has(item.id));
  }, [stableFeedData, clusterIds]);

  /**
   * Zaškrtnuté bunky, ktoré sa naozaj uplatnia. V novinkách sú to žiadne: bunky
   * sú stavy podnetu, takže `matchesStat` novinku nepustí do ani jednej a zoznam
   * by vyšiel prázdny. Panel ich v tom segmente ani nekreslí.
   *
   * Je to výpočet, nie ďalší stav - zaškrtnutie prežije odchod na novinky
   * a návrat, presne ako ho prežije reload.
   */
  const activeStatFilters = React.useMemo(
    () => (segment === 'reports' ? statFilters : []),
    [segment, statFilters],
  );

  /**
   * Zoznam, ktorý vidí panel: `statItems` zúžený zaškrtnutými bunkami, teda aj
   * o vybranú oblasť. Mapa berie tie isté bunky, ale bez oblasti - viď
   * `feedsWithLocation`.
   */
  const panelItems = React.useMemo(
    () => statItems.filter((item) => matchesStats(item, activeStatFilters)),
    [statItems, activeStatFilters],
  );

  /**
   * Značky na mape: to isté, čo má panel, len bez položiek bez polohy.
   *
   * **Zaškrtnuté bunky štatistiky orezávajú aj mapu**, nie len zoznam. Kým to
   * tak nebolo, značky zostávali všetky, aby bolo vidno, čo filter skryl - ale
   * to znamenalo, že vyfiltrovaný zoznam a mapa vedľa neho hovorili o dvoch
   * rôznych množinách a nedalo sa podľa mapy odhadnúť, čoho je v okolí koľko.
   *
   * Vybraná oblasť (`clusterIds`) mapu **neorezáva**, hoci zoznam áno: je to
   * rozsah, nie filter. Keby mapa brala `panelItems`, klik do zoskupenia by
   * z nej odstránil všetko okolo neho, teda aj ostatné zoskupenia, na ktoré sa
   * dá klikať ďalej.
   *
   * Nefiltrujú sa pri otvorenom detaile na vybranú položku, ako to robil popup
   * - detail je obsah panela, takže mapa pod ním nemá dôvod zostať prázdna.
   *
   * A **nedržia si vlastné „predchádzajúce" dáta**. `stableFeedData` počas
   * dotazu na predošlé samo padá, takže druhá záloha tu len spôsobila, že mapa
   * a panel hovorili o inom zozname: klik do značky, ktorú panel už nepoznal,
   * zapísal do adresy detail, ktorý sa nemal ako otvoriť.
   */
  const feedsWithLocation = React.useMemo(
    () =>
      stableFeedData
        .filter((item) => matchesStats(item, activeStatFilters))
        .filter(hasLocation),
    [stableFeedData, activeStatFilters],
  );

  /**
   * Detail vybranej značky. Dáta berie z feedu, ktorý už je načítaný, takže sa
   * panel otvorí hneď a bez ďalšieho dotazu na API.
   *
   * Naposledy zobrazená položka sa pritom drží v refe a použije sa, keď ju feed
   * prestane vracať. Stane sa to bežne: pri otvorenom detaile stačí odsunúť
   * mapu tak, aby podnet vypadol z dotazu, a panel by inak zhasol, kým by
   * `?detail=` v adrese zostalo - vtedy už tú istú značku nešlo otvoriť, lebo
   * klik do nej adresu nemenil. Zálohu plní aj samotný klik do mapy (mapa
   * posiela celú položku), takže nezáleží na tom, či ju panel v tej chvíli má.
   */
  const selectedItemRef = React.useRef<IFeedItem | undefined>(undefined);
  const selectedItem = React.useMemo(() => {
    if (!selectedShortId) return undefined;

    const found = stableFeedData.find(
      ({ id }) => shortId(id) === selectedShortId,
    );
    if (found) return found;

    const cached = selectedItemRef.current;
    return cached && shortId(cached.id) === selectedShortId
      ? cached
      : undefined;
  }, [stableFeedData, selectedShortId]);

  React.useEffect(() => {
    if (selectedItem) {
      selectedItemRef.current = selectedItem;
    }
  }, [selectedItem]);

  /**
   * Starý tvar odkazu na podnet - `/?detail=<ID>` alebo `/podnety/<URN>` -
   * sa prepíše na `/podnety/<krátke ID>`, aby mal podnet v adrese vždy tú
   * istú podobu. Pri `?detail=` sa čaká na položku: až z nej je jasné, či je
   * to podnet, alebo novinka, ktorá na `?detail=` zostáva.
   */
  React.useEffect(() => {
    const legacyDetail =
      !pathReportId && selectedItem && isReportId(selectedItem.id);
    const longPath = pathReportId && pathReportId !== shortId(pathReportId);
    if (legacyDetail || longPath) {
      setSelectedNewsItemId(legacyDetail ? selectedItem.id : pathReportId);
    }
  }, [pathReportId, selectedItem, setSelectedNewsItemId]);

  /**
   * Detail je obsah panela, takže na mobile musí byť list vytiahnutý - do
   * zatvoreného (184 px) sa nezmestí ani jeho hlavička.
   *
   * Kliknutia si to hovoria samy (`handleSelectItem`, `handleItemMapClick`),
   * ale **odkaz s `?detail=` žiadne kliknutie nemá** - a odkazy na podnety
   * chodia z e-mailov aj z notifikácií a od augusta 2026 na mape končia
   * všetky, lebo podnet vlastnú stránku nemá ([[ReportDetailRedirect]]).
   * Bez tohto sa z takého odkazu na telefóne otvoril zatvorený list, v ktorom
   * bolo z detailu vidieť pás fotky.
   *
   * Závislosťou je **identifikátor z adresy**, nie položka: fire sa tak raz na
   * otvorený detail a list sa dá potom ručne zavrieť, kým detail zostáva.
   */
  React.useEffect(() => {
    if (selectedShortId) {
      setSheetExpanded(true);
    }
  }, [selectedShortId, setSheetExpanded]);

  /**
   * Žiadosť mape, aby sa vrátila na priblíženie, ktoré bolo pred otvorením
   * detailu. Je to počítadlo: druhé zatvorenie musí mapu vrátiť znova.
   */
  const [restoreZoomToken, setRestoreZoomToken] = React.useState(0);

  const closeDetail = React.useCallback(() => {
    setSelectedNewsItemId(undefined);
    setFocusRequest(undefined);
    setRestoreZoomToken((token) => token + 1);
  }, [setSelectedNewsItemId]);

  /**
   * Detail zavretý adresou, nie panelom - „späť" v prehliadači alebo gesto
   * na telefóne. `closeDetail` vtedy nebežal, takže mapa by ostala v ulici
   * a žiadosť o presun by visela (moj-presov#24: po návrate začínal zoznam
   * navrchu). Tu sa dorobí to isté, čo robí šípka v paneli. Po šípke beží
   * tiež, ale nič nespraví: pohľad pred detailom už mapa vrátila a zahodila.
   */
  const previousSelectedShortId = React.useRef(selectedShortId);
  React.useEffect(() => {
    const previous = previousSelectedShortId.current;
    previousSelectedShortId.current = selectedShortId;
    if (previous && !selectedShortId) {
      setFocusRequest(undefined);
      setRestoreZoomToken((token) => token + 1);
    }
  }, [selectedShortId]);

  // Aj klik do mapy je žiadosť o presun: značka ide do stredu a mapa sa
  // priblíži, rovnako ako pri výbere zo zoznamu. Nová žiadosť pri každom
  // kliknutí je zámer - druhé kliknutie na tú istú značku ju vycentruje znova,
  // keď medzitým používateľ mapu odsunul.
  const handleSelectItem = React.useCallback(
    (item?: IFeedItem) => {
      // Mapa posiela celú položku, takže sa nemusí hľadať v zozname panela.
      // Kým sa hľadala, dala sa appka zaseknúť: značka, ktorú panel v tej
      // chvíli nepoznal, zapísala do adresy detail, ktorý sa nemal ako
      // otvoriť, a ďalšie kliknutia do nej už adresu nemenili.
      if (item) {
        selectedItemRef.current = item;
      }
      setSelectedNewsItemId(item?.id);
      setFocusRequest(item ? { id: item.id } : undefined);
      // Detail je obsah panela, takže na mobile musí byť list vytiahnutý -
      // do zatvoreného (184 px) by sa nezmestila ani jeho hlavička.
      if (item) {
        setSheetExpanded(true);
      }
    },
    [setSelectedNewsItemId, setSheetExpanded],
  );

  /**
   * Klik na zoskupenie zúži zoznam na to, čo je v ňom.
   *
   * Na mobile pritom **vytiahne list**: zúžený zoznam je v zatvorenom liste
   * (184 px, vojde sa doň len prepínač segmentu) neviditeľný, takže to
   * vyzeralo, že klik na číslo v zhluku nič neurobil. A zavrie otvorený detail
   * - zoskupenie je výber oblasti, nie jednej značky.
   */
  const handleSelectCluster = React.useCallback(
    (itemIds?: string[]) => {
      setClusterIds(itemIds ?? null);
      if (itemIds?.length) {
        setSelectedNewsItemId(undefined);
        setFocusRequest(undefined);
        setSheetExpanded(true);
      }
    },
    [setSheetExpanded, setSelectedNewsItemId],
  );

  // Klik na položku v zozname: mapa sa na ňu presunie a otvorí sa jej detail.
  // To isté robí aj tlačidlo „Zobraziť na mape" v tej istej karte.
  const handleItemMapClick = React.useCallback(
    (id: string) => {
      setSelectedNewsItemId(id);
      setFocusRequest({ id });
      // Detail sa otvára **v paneli**, takže na mobile musí byť list vytiahnutý
      // - do zatvoreného (184 px) by sa nezmestil nič než rukoväť. Predtým sa
      // zoznam naopak zatváral, lebo detail bol samostatný modálny sheet.
      setSheetExpanded(true);
    },
    [setSheetExpanded, setSelectedNewsItemId],
  );

  const updateSegment = React.useCallback((next: FeedSegmentValue) => {
    localStorage.setItem(config.LOCAL_STORAGE.FEED_SEGMENT, next);
    setSegment(next);
    // Bunky štatistiky sa **nenulujú**. Patria podnetom a v novinkách by zoznam
    // zúžili na prázdno (`matchesStat` novinku nepustí do žiadnej bunky), ale to
    // rieši `activeStatFilters` - v novinkách sa neuplatnia. Keď sa nulovali,
    // odchod na novinky a späť zhodil zaškrtnutie, ktoré zostalo uložené
    // v `localStorage`, takže po F5 sa vrátilo a vyzeralo to ako chyba
    // perzistencie.
    //
    // Vybraná oblasť sa naopak zahodiť **musí**: zoskupenie sa skladalo
    // z konkrétnych podnetov a v novinkách by zoznam zúžilo na nič.
    setClusterIds(null);
  }, []);

  const sortLabel = React.useMemo(
    () =>
      sortOptions.find(
        (option) =>
          option.orderBy === sortOrder.orderBy &&
          option.orderDirection === sortOrder.orderDirection,
      )?.label,
    [sortOrder],
  );

  const updateSortOrder = (newOrder: FeedSortOrderValues) => {
    localStorage.setItem(
      config.LOCAL_STORAGE.FEED_SORT_ORDER,
      JSON.stringify(newOrder),
    );
    setSortOrder(newOrder);
  };

  const hasNoNewsPreferences =
    !isLoadingUserPreferences &&
    authState === 'authorized' &&
    (!dataUserPreferences || dataUserPreferences.length === 0) &&
    segment === 'news';

  return (
    <Box sx={{ height: 1, width: 1, position: 'relative', overflow: 'hidden' }}>
      {/* Vlastný stacking context. Prepínač Zoznam/Mapa aj prekryvy mapy majú
          `zIndex` v tisícoch (ArcGIS widgety idú vysoko), ale shell z
          `ui-react` stavia svoje vrstvy na jednociferných číslach - hlavička 6,
          spodný sheet 10 - a to všetko v jednom kontexte koreňa shellu. Bez
          tohto `zIndex` by prekryvy mapy prekryli otvorené mobilné menu (aj
          hlavičku), lebo 1000 > 10. */}
      <Box
        // Mapa má vlastné ťahanie - nesmie ho zjesť obnovenie
        // stránky ťahom nadol (`Pulldown`).
        data-pulldown-blocked='true'
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          '.esri-attribution': {
            bottom: -8,
            left: -8,
            right: -8,
            fontSize: 10,
          },
        }}
      >
        <FeedsMap
          onMapCenterChange={handleMapCenterChange}
          onZoom={setZoom}
          focusRequest={focusRequest}
          onSearchLocationChange={(location) => {
            setSearchLocation(location);
          }}
          onSelectCluster={handleSelectCluster}
          onSearchReport={handleItemMapClick}
          onViewReady={setMapView}
          searchLocation={queryLocation || searchLocation}
          feedsWithLocation={feedsWithLocation}
          forceShowMap={
            isFeedError || (!isFeedLoading && !feedsWithLocation.length)
          }
          isLoading={!shouldShowLoading && isFeedLoading}
          onSelectItem={handleSelectItem}
          selectedItem={selectedItem}
          restoreZoomToken={restoreZoomToken}
          // Poloha z adresy ide do mapy **už pri prvom renderi**, nie až
          // cez `mapCenter`, ktorý sa plní v efekte. Mapa bez počiatočnej
          // polohy si ju totiž vezme zo zariadenia, a tá príde neskôr než
          // let na plus kód - odkaz otvorený v novej karte potom skončil na
          // polohe používateľa, nie pri podnete.
          initialCenter={
            placeRequest
              ? { lng: placeRequest.center[0], lat: placeRequest.center[1] }
              : mapCenter
          }
          initialZoom={placeRequest?.zoom ?? zoom}
          placeRequest={placeRequest}
          // Na desktope stojí tlačidlo nového podnetu v riadku vedľa
          // hľadania, nie v rohu mapy. Mapa dostane hotový uzol - o tom, čo
          // sa podáva, nevie nič.
          searchAction={
            isMd ? <NewReportFab className='screen-only' inline /> : undefined
          }
          // Na mobile výška **zatvoreného** listu aj pri vytiahnutom: okraj
          // pohľadu sa so zmenou posunie celou mapou, a skákať pri každom
          // ťahu listu nemá.
          viewPadding={
            isMd ? { right: feedPanelInset } : { bottom: SHEET_COLLAPSED }
          }
          // Pri otvorenom detaile patrí stred mapy vybranej značke - pin by
          // stál nad ňou a čítal sa ako ďalšia značka. Vytiahnutý list na
          // mobile ho zase prekrýva.
          showCenterPin={!selectedItem && (isMd || !sheetExpanded)}
        />
      </Box>

      {/* Desktop: panel je plávajúca karta nad mapou, nie zadokovaný stĺpec.
          Preto je `position: absolute` a nie flex položka - mapa pod ním ide
          na plnú plochu a nekončí na jeho hrane. */}
      {isMd ? (
        <Box
          ref={feedPanelRef}
          className='screen-only'
          sx={(theme) => ({
            position: 'absolute',
            top: 20,
            right: 20,
            bottom: 20,
            zIndex: 2,
            width: 412,
            maxWidth: 'calc(100% - 40px)',
            display: 'flex',
            flexDirection: 'column',
            padding: 2,
            borderRadius: `${theme.typography.pxToRem(12)}`,
            backgroundColor: theme.palette.background.paper,
            border: `${theme.typography.pxToRem(1)} solid ${
              theme.palette.divider
            }`,
            boxShadow: '0 10px 30px rgba(23, 21, 18, 0.12)',
          })}
        >
          {/* Biely výstupok na hrane panela - hrot, do ktorého sa zapichne
              čiara od značky. Kreslí ho panel, nie mapa: je to jeho špic, takže
              musí ležať nad čiarou (panel je nad mapou) a musí prekryť aj svoj
              vlastný rám, preto je posunutý o pixel doň. Stojí v polovici
              výšky, kde je čiara pevne ukotvená. */}
          {selectedItem ? (
            <Box
              aria-hidden
              sx={(theme) => ({
                position: 'absolute',
                left: theme.typography.pxToRem(-11),
                top: '50%',
                width: 0,
                height: 0,
                transform: 'translateY(-50%)',
                borderTop: `${theme.typography.pxToRem(9)} solid transparent`,
                borderBottom: `${theme.typography.pxToRem(9)} solid transparent`,
                borderRight: `${theme.typography.pxToRem(12)} solid ${
                  theme.palette.background.paper
                }`,
              })}
            />
          ) : null}

          {selectedItem ? (
            <FeedDetail
              item={selectedItem}
              view={mapView}
              onBack={closeDetail}
            />
          ) : (
            <FeedPanel
              items={panelItems}
              countItems={statItems}
              isLoading={shouldShowLoading}
              segment={segment}
              onSegmentChange={updateSegment}
              statFilters={statFilters}
              onStatToggle={toggleStatFilter}
              cluster={clusterIds ? { count: panelItems.length } : null}
              onClearCluster={() => setClusterIds(null)}
              sortLabel={sortLabel}
              onSortClick={() => setSortOrderOpen(true)}
              onItemMapClick={handleItemMapClick}
              hasNoNewsPreferences={hasNoNewsPreferences}
              hasNextPage={hasNextPage}
              fetchNextPage={fetchNextPage}
              isFetchingNextPage={isFetchingNextPage}
              searchLocation={queryLocation || searchLocation}
              scrollStore={feedScroll}
            />
          )}
        </Box>
      ) : (
        <FeedSheet
          expanded={sheetExpanded}
          onExpandedChange={setSheetExpanded}
          handleLabel={__('Zoznam podnetov')}
        >
          {selectedItem ? (
            <FeedDetail
              compact
              item={selectedItem}
              view={mapView}
              onBack={closeDetail}
            />
          ) : (
            <FeedPanel
              compact
              collapsed={!sheetExpanded}
              items={panelItems}
              countItems={statItems}
              isLoading={shouldShowLoading}
              segment={segment}
              onSegmentChange={updateSegment}
              statFilters={statFilters}
              onStatToggle={toggleStatFilter}
              cluster={clusterIds ? { count: panelItems.length } : null}
              onClearCluster={() => setClusterIds(null)}
              sortLabel={sortLabel}
              onSortClick={() => setSortOrderOpen(true)}
              onItemMapClick={handleItemMapClick}
              hasNoNewsPreferences={hasNoNewsPreferences}
              hasNextPage={hasNextPage}
              fetchNextPage={fetchNextPage}
              isFetchingNextPage={isFetchingNextPage}
              searchLocation={queryLocation || searchLocation}
              scrollStore={feedScroll}
            />
          )}
        </FeedSheet>
      )}

      {isMd ? <FeedLegend /> : null}

      {/* Na mobile zostáva tlačidlo plávajúce nad mapou (na desktope je v riadku
          s hľadaním, viď `searchAction`). Viditeľné je aj pri otvorenom detaile:
          ten je obsahom panela, nie bublinou nad mapou, takže mu nemá čo
          prekážať. */}
      {isMd ? null : (
        <NewReportFab
          className='screen-only'
          // Posúva sa nad spodný list - vytiahnutý list má 76 %, zatvorený
          // 184 px. Prechod je ten istý ako pri liste, takže sa hýbu spolu.
          mobileBottom={sheetExpanded ? `calc(${SHEET_EXPANDED} + 12px)` : 196}
        />
      )}

      <FeedSortOrder
        open={sortOrderOpen}
        onClose={() => setSortOrderOpen(false)}
        onSubmit={(values) => {
          updateSortOrder(values);
        }}
        initialValues={sortOrder}
      />
    </Box>
  );
};

export default HomePage;
